import { OpenFeature, ProviderEvents } from "@openfeature/web-sdk";
import { FlagdWebProvider } from "@openfeature/flagd-web-provider";
import DashboardConfig from "@/models/DashboardConfig";
import AppNotification from "@/models/AppNotification";

/**
 * FeatureFlagService
 *
 * Wrapper around OpenFeature's web SDK and the flagd web provider.
 * - Initializes the provider and client
 * - Manages the evaluation context (e.g., env, hostname, clientId)
 * - Exposes helpers to fetch typed flag values
 * - Handles teardown for tests/navigation resets
 *
 * Environment variables used for provider configuration:
 * - VUE_APP_FLAGD_HOST
 * - VUE_APP_FLAGD_PORT
 * - VUE_APP_FLAGD_TLS ("true" | "false")
 *
 * Example:
 *  await FeatureFlagService.initialize();
 *  await FeatureFlagService.setContext({ env: 'dev', hostname: 'localhost', clientId: 123 });
 *  const enabled = FeatureFlagService.getFeatureFlagValue('some-flag');
 */

/**
 * @typedef {Object} InitializeOptions
 * @property {(eventDetail: any) => void} [onReady]
 * @property {(eventDetail: any) => void} [onError]
 * @property {(eventDetail: any) => void} [onConfigurationChanged]
 * @property {(eventDetail: any) => void} [onContextChanged]
 * @property {(eventDetail: any) => void} [onReconciling]
 * @property {(eventDetail: any) => void} [onStale]
 */

/**
 * @typedef {'boolean'|'string'|'number'|'object'} FlagType
 */

let client = null;
let isReady = false;

/**
 * OpenFeature domain for the client-scoped flag set.
 *
 * The default flag set is evaluated once at login against the signed-in user's own
 * clientId, which is the right answer everywhere except the admin screens: there an
 * internal admin picks a client out of the dropdown and configures it, so "is this
 * feature on" means the SELECTED client, not their own.
 *
 * It has to be a second provider rather than a second read, because the flagd web
 * provider holds one cache for one context: `resolveBooleanEvaluation(flagKey)` takes
 * no context at all, and `onContextChange` discards the whole cache and refetches.
 * Binding a provider to a domain gives that domain its own cache and its own context,
 * so `setClientScopedContext` refreshes this set alone and leaves every other flag in
 * the app evaluating exactly as it did before.
 */
const CLIENT_SCOPED_DOMAIN = "client-scoped";

let clientScopedClient = null;
let clientScopedReady = false;

const FeatureFlagService = {
  /**
   * Initialize OpenFeature and the flagd provider, and set up provider event handlers.
   * @param {InitializeOptions} [options] Optional callbacks for provider events.
   * @returns {Promise<void>}
   */
  async initialize({
    onReady,
    onError,
    onConfigurationChanged,
    onContextChanged,
    onReconciling,
    onStale,
  }) {
    // isReady is a module-level flag that otherwise only resets inside kill().
    // Reset it here too so a fresh initialize() call never inherits a stale
    // `true` left over from an earlier connection in the same session — that
    // stale value let setFeatureFlags() (dispatched from three separate call
    // sites) resolve instantly with default flags before this attempt's own
    // connection had actually succeeded.
    isReady = false;

    // maxRetries bounds the provider's internal exponential-backoff reconnect
    // loop (default Infinity — see node_modules/@openfeature/flagd-web-provider),
    // so a persistently unreachable connection reaches ProviderEvents.Error
    // instead of retrying forever with no terminal signal.
    const provider = new FlagdWebProvider({
      host: process.env.VUE_APP_FLAGD_HOST,
      port: process.env.VUE_APP_FLAGD_PORT,
      tls: process.env.VUE_APP_FLAGD_TLS === "true",
      maxRetries: 3,
    });

    await OpenFeature.setProviderAndWait(provider);
    client = OpenFeature.getClient();

    client.addHandler(ProviderEvents.Ready, (eventDetail) => {
      isReady = true;
      typeof onReady === "function" && onReady(eventDetail);
    });

    client.addHandler(ProviderEvents.Error, (eventDetail) => {
      typeof onError === "function" && onError(eventDetail);
    });

    client.addHandler(ProviderEvents.ConfigurationChanged, (eventDetail) => {
      typeof onConfigurationChanged === "function" && onConfigurationChanged(eventDetail);
    });

    client.addHandler(ProviderEvents.ContextChanged, (eventDetail) => {
      typeof onContextChanged === "function" && onContextChanged(eventDetail);
    });

    client.addHandler(ProviderEvents.Reconciling, (eventDetail) => {
      typeof onReconciling === "function" && onReconciling(eventDetail);
    });

    client.addHandler(ProviderEvents.Stale, (eventDetail) => {
      typeof onStale === "function" && onStale(eventDetail);
    });
  },

  /**
   * Get the active OpenFeature client instance.
   * @returns {Client} The OpenFeature client, or null if not initialized.
   */
  getClient() {
    return client;
  },

  /**
   * Whether the provider has finished its ready-handshake (i.e. the
   * ProviderEvents.Ready event has fired since the last initialize()/kill()).
   * Callers should treat flag values as untrustworthy until this is true:
   * getFeatureFlagValue() returns `false` both when a flag is genuinely off
   * AND when the SDK isn't ready yet, so this is the only way to tell them apart.
   * @returns {boolean} True once ready, false before initialize() or after kill().
   */
  isReady() {
    return isReady;
  },

  /**
   * Set the evaluation context used by the provider for targeting rules.
   * Common keys used by this app: { env: string, hostname: string, clientId: number|string }.
   * @param {Object} context
   * @returns {Promise<void>}
   */
  async setContext(context) {
    return await OpenFeature.setContext(context);
  },

  /**
   * Clear the current evaluation context.
   * @returns {Promise<void>}
   */
  async clearContext() {
    return await OpenFeature.clearContext();
  },

  /**
   * Initialize the client-scoped provider, bound to {@link CLIENT_SCOPED_DOMAIN}.
   *
   * Lazy on purpose: this opens a second streaming connection to flagd, and only the
   * admin screens need it. Call it from the first surface that reads a client-scoped
   * flag; everywhere else pays nothing.
   *
   * **The context is applied AFTER the provider is bound, and the order is
   * load-bearing.** `FlagdWebProvider.initialize()` only opens the stream — it does
   * not populate the flag cache. That cache is filled by `fetchAll()`, whose only
   * non-stream caller is `onContextChange()`. Setting the context first therefore
   * means no context CHANGE ever occurs for this provider, `fetchAll` never runs, and
   * `evaluate()` throws FlagNotFoundError on an empty cache — which the SDK turns into
   * the caller's default. Every flag reads off, indistinguishably from "not
   * allowlisted".
   *
   * This is the same order the app-wide set uses in `setFeatureFlags`, which sets its
   * context after initialization and has always worked.
   *
   * Idempotent: a second call with a new context just moves the context.
   *
   * @param {Object} context Evaluation context, e.g. { env, clientId }.
   * @returns {Promise<void>}
   */
  async initializeClientScoped(context) {
    if (clientScopedClient) {
      return await this.setClientScopedContext(context);
    }

    const provider = new FlagdWebProvider({
      host: process.env.VUE_APP_FLAGD_HOST,
      port: process.env.VUE_APP_FLAGD_PORT,
      tls: process.env.VUE_APP_FLAGD_TLS === "true",
    });

    await OpenFeature.setProviderAndWait(CLIENT_SCOPED_DOMAIN, provider);
    clientScopedClient = OpenFeature.getClient(CLIENT_SCOPED_DOMAIN);

    clientScopedClient.addHandler(ProviderEvents.Ready, () => {
      clientScopedReady = true;
    });
    clientScopedClient.addHandler(ProviderEvents.Error, () => {
      clientScopedReady = false;
    });

    // The Ready handler above only fires for readiness reached AFTER it is attached,
    // and setProviderAndWait has already resolved by this point.
    clientScopedReady = true;

    // Last, and only now that a provider is bound to the domain: this is the context
    // CHANGE that makes the provider fetch its flags. Without it the cache is empty
    // and the first read of every flag returns its default — the bug that made the
    // Inform tab show its flag-off layout until an admin navigated away and back,
    // because only that second visit took the setClientScopedContext path.
    await OpenFeature.setContext(CLIENT_SCOPED_DOMAIN, context);
  },

  /**
   * Whether the client-scoped provider has been initialized.
   *
   * Lets a caller skip a refresh rather than opening the connection as a side effect
   * of something unrelated, such as the client dropdown moving in a non-admin session.
   * @returns {boolean}
   */
  isClientScopedInitialized() {
    return !!clientScopedClient;
  },

  /**
   * Move the client-scoped evaluation context.
   *
   * The provider refetches its whole cache on a context change, so this is what makes
   * the client-scoped set follow the selected client. No-op until
   * {@link initializeClientScoped} has run.
   * @param {Object} context
   * @returns {Promise<void>}
   */
  async setClientScopedContext(context) {
    if (!clientScopedClient) return;
    return await OpenFeature.setContext(CLIENT_SCOPED_DOMAIN, context);
  },

  /**
   * Read a flag from the client-scoped set.
   *
   * Returns the same "unavailable" defaults as {@link getFeatureFlagValue}: a flag that
   * cannot be evaluated reads off rather than throwing, so a flagd outage leaves an
   * admin screen on its existing behaviour instead of breaking it.
   * @param {string} flagKey
   * @param {FlagType} [flagType='boolean']
   * @returns {boolean|string|number|Object}
   */
  getClientScopedFeatureFlagValue(flagKey, flagType = "boolean") {
    if (!clientScopedClient || !clientScopedReady) {
      return flagType === "string"
        ? ""
        : flagType === "number"
        ? 0
        : flagType === "object"
        ? {}
        : false;
    }

    switch (flagType) {
      case "string":
        return clientScopedClient.getStringValue(flagKey, "");
      case "number":
        return clientScopedClient.getNumberValue(flagKey, 0);
      case "object":
        return clientScopedClient.getObjectValue(flagKey, {});
      case "boolean":
      default:
        return clientScopedClient.getBooleanValue(flagKey, false);
    }
  },

  /**
   * Get a flag value with the desired type.
   * @param {string} flagKey The flag key to evaluate.
   * @param {FlagType} [flagType='boolean'] The expected type of the flag value.
   * @returns {boolean|string|number|Object} The evaluated flag value, or a sensible default if unavailable.
   */
  getFeatureFlagValue(flagKey, flagType = "boolean") {
    if (!client || !isReady) {
      return false;
    }

    let flagValue;
    switch (flagType) {
      case "boolean":
        flagValue = client.getBooleanValue(flagKey, false);
        break;
      case "string":
        flagValue = client.getStringValue(flagKey, "");
        break;
      case "number":
        flagValue = client.getNumberValue(flagKey, 0);
        break;
      case "object":
        flagValue = client.getObjectValue(flagKey, {});
        break;
      default:
        flagValue = false;
    }

    return flagValue;
  },

  /**
   * Get the dashboard configuration from the feature flag.
   * Reads the "feature-program-dashboard-config" object flag and returns
   * a DashboardConfig instance. Never throws, never returns null/undefined.
   * @returns {DashboardConfig}
   */
  getDashboardConfig() {
    if (!client || !isReady) {
      return new DashboardConfig();
    }
    try {
      const data = client.getObjectValue("feature-program-dashboard-config", {});
      return DashboardConfig.fromFlagData(data);
    } catch (_) {
      return new DashboardConfig();
    }
  },

  /**
   * Get program banner data from the feature flag.
   * @returns {AppNotification|null}
   */
  getAppNotificationData() {
    if (!client || !isReady) return null;
    try {
      const data = client.getObjectValue("feature-platform-banner-web", {});
      if (!data?.enabled) return null;
      return AppNotification.fromFlagData(data);
    } catch (_) {
      // Null rather than a default instance: Programs.vue renders on
      // `v-if="appNotification"`, so a truthy empty banner would paint an
      // empty bar. This matches the existing "unavailable" contract above.
      return null;
    }
  },

  /**
   * Tear down provider and context; useful for tests or when re-initializing.
   * @returns {Promise<void>}
   */
  async kill() {
    await OpenFeature.clearProviders();
    // clearContexts, not clearContext: the latter resets only the global context and
    // would leave the client-scoped domain's context behind for the next session.
    await OpenFeature.clearContexts();
    client = null;
    isReady = false;
    clientScopedClient = null;
    clientScopedReady = false;
  },
};

export default FeatureFlagService;
